/**
 * Aviso de cambios sin guardar.
 *
 * Cubre las dos formas de perder trabajo:
 *
 * 1. **Salir del sitio** (recargar, cerrar la pestaña): `beforeunload`, que es lo
 *    único que el navegador permite y muestra su propio cartel.
 * 2. **Navegar dentro de la aplicación** (un enlace del menú, una miga de pan):
 *    se intercepta el click en fase de captura, se cancela y se pregunta con
 *    nuestro propio diálogo.
 *
 * El punto 2 se hace con un listener de documento y no con `useBlocker` del
 * router porque `useBlocker` exige un router de datos (`createBrowserRouter`) y
 * la aplicación monta un `<BrowserRouter>` clásico. Cambiar eso es tocar el
 * shell, que es de otro dueño; esto resuelve el mismo problema sin invadirlo.
 *
 * Solo se interceptan enlaces internos con botón principal y sin modificadores:
 * un ctrl+click abre otra pestaña y no pierde nada, así que no se molesta al
 * usuario.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface DirtyGuard {
  /** Destino pendiente de confirmación, o `null` si no hay ninguno. */
  destinoPendiente: string | null;
  /** Confirma la salida y navega. */
  confirmar: () => void;
  /** Se queda donde está. */
  cancelar: () => void;
  /** Navegación propia del formulario (volver, cancelar), con el mismo aviso. */
  navegarConAviso: (destino: string) => void;
}

export function useDirtyGuard(sucio: boolean): DirtyGuard {
  const navegar = useNavigate();
  const [destinoPendiente, setDestinoPendiente] = useState<string | null>(null);
  const sucioRef = useRef(sucio);
  sucioRef.current = sucio;

  useEffect(() => {
    if (!sucio) return;

    const alSalir = (evento: BeforeUnloadEvent): void => {
      evento.preventDefault();
      // Los navegadores modernos ignoran el texto, pero exigen `returnValue`.
      evento.returnValue = '';
    };
    window.addEventListener('beforeunload', alSalir);
    return () => {
      window.removeEventListener('beforeunload', alSalir);
    };
  }, [sucio]);

  useEffect(() => {
    const alHacerClick = (evento: MouseEvent): void => {
      if (!sucioRef.current) return;
      if (evento.defaultPrevented || evento.button !== 0) return;
      if (evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.altKey) return;

      const objetivo = evento.target as Element | null;
      const ancla = objetivo?.closest?.('a');
      if (!(ancla instanceof HTMLAnchorElement)) return;
      if (ancla.target !== '' && ancla.target !== '_self') return;
      if (ancla.hasAttribute('download')) return;

      const destino = new URL(ancla.href, window.location.href);
      if (destino.origin !== window.location.origin) return;

      // Desde `file://` el pathname es la ruta del archivo en el disco
      // (`/C:/Users/…/Nucleo-ERP-sistema.html`) y las rutas viven en el hash.
      // Componer `pathname + hash` mandaria a quien deja un formulario a medio
      // llenar a un «no encontrado». El interruptor lo fija el build offline; en
      // el build normal la condicion es `false` y la rama se va del bundle.
      const soloHash = import.meta.env.VITE_NUCLEO_OFFLINE === '1';
      // SIN el `#` adelante, y ese detalle es todo el arreglo. Lo que se guarda
      // acá termina en `navigate()`, que espera una RUTA (`/m/base.partner`); si
      // se le pasa `#/m/base.partner` lo interpreta como un ancla dentro de la
      // página actual y no navega a ningún lado.
      //
      // El síntoma era exactamente ése: en el archivo suelto, «Salir sin
      // guardar» no salía. El diálogo se cerraba, el formulario seguía ahí, y
      // había que subir hasta la cabecera a apretar «Cancelar». Un botón que no
      // hace lo que dice es peor que no tenerlo.
      const sinNumeral = (hash: string): string => (hash.startsWith('#') ? hash.slice(1) : hash) || '/';
      const aqui = soloHash
        ? sinNumeral(window.location.hash)
        : `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const ruta = soloHash
        ? sinNumeral(destino.hash)
        : `${destino.pathname}${destino.search}${destino.hash}`;
      if (ruta === aqui) return;

      evento.preventDefault();
      evento.stopPropagation();
      setDestinoPendiente(ruta);
    };

    document.addEventListener('click', alHacerClick, true);
    return () => {
      document.removeEventListener('click', alHacerClick, true);
    };
  }, []);

  return {
    destinoPendiente,
    confirmar: () => {
      const destino = destinoPendiente;
      setDestinoPendiente(null);
      sucioRef.current = false;
      if (destino !== null) navegar(destino);
    },
    cancelar: () => {
      setDestinoPendiente(null);
    },
    navegarConAviso: (destino: string) => {
      if (sucioRef.current) setDestinoPendiente(destino);
      else navegar(destino);
    },
  };
}
